import React from 'react';
import { motion } from 'framer-motion';
import { mockPreviousWinners } from '../data/mockData';
import { useAuth } from '../context/AuthContext';
import { resolveProfilePic, handleProfilePicError } from '../lib/avatar';

const getWinnerTime = (winner) => {
  const value = winner?.winnerAt || winner?.date;
  const time = value ? new Date(value).getTime() : 0;
  return Number.isNaN(time) ? 0 : time;
};

const getWinnerDisplayAmount = (winner) => Math.floor(
  winner?.profileScoreWon ?? winner?.amountWon ?? winner?.rewardAmount ?? winner?.resultAmount ?? winner?.profileScore ?? 0
);

const formatWinnerDate = (winner) => {
  const value = winner?.date || winner?.winnerAt;
  const date = value ? new Date(value) : null;
  if (!date || Number.isNaN(date.getTime())) {
    return '';
  }
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

// One winner rendered inside a split (two-winner) card. Compact layout: avatar
// on the left; name, @username, score and a very tiny date stacked to its right.
const WinnerHalf = ({ winner, onUserClick, testId }) => (
  <button
    type="button"
    onClick={() => onUserClick(winner)}
    data-testid={testId}
    className="flex-1 min-w-0 flex items-center gap-2 text-left px-1"
  >
    <img
      src={resolveProfilePic(winner.profilePic)}
      alt={winner.name}
      onError={handleProfilePicError}
      className="w-9 h-9 rounded-full object-cover border-2 border-white flex-shrink-0"
    />
    <div className="flex-1 min-w-0">
      <h3 className="text-[13px] leading-4 font-semibold text-[#000000] font-['Outfit'] truncate">
        {winner.name}
      </h3>
      <p className="text-[10px] leading-3 text-[#666666] font-['DM_Sans'] truncate">
        {winner.username}
      </p>
      <div className="flex items-baseline justify-between gap-3 mt-0.5">
        <p className="text-sm leading-4 font-bold text-gradient font-['Outfit']">
          {getWinnerDisplayAmount(winner)}
        </p>
        <p className="text-[9px] leading-3 text-[#999999] font-['DM_Sans'] flex-shrink-0">
          {formatWinnerDate(winner)}
        </p>
      </div>
    </div>
  </button>
);

const PreviousWinners = ({ onUserClick }) => {
  const { previousWinners } = useAuth();

  const sourceList = (previousWinners?.length ? previousWinners : mockPreviousWinners)
    .slice()
    .sort((a, b) => getWinnerTime(b) - getWinnerTime(a));

  // Group winners by draw date so a date with TWO winners renders as ONE split
  // card, while single-winner dates keep the original card exactly.
  const groupsByDate = new Map();
  const orderedGroups = [];
  sourceList.forEach((winner) => {
    const key = winner?.date || winner?.winnerAt || winner?.id;
    if (!groupsByDate.has(key)) {
      const group = { key, winners: [] };
      groupsByDate.set(key, group);
      orderedGroups.push(group);
    }
    groupsByDate.get(key).winners.push(winner);
  });

  // Within a two-winner group, order by winnerSlot so Winner A (slot 0) is on
  // the left; fall back to win time for any legacy rows without a slot.
  orderedGroups.forEach((group) => {
    group.winners.sort((a, b) => {
      const sa = a?.winnerSlot ?? null;
      const sb = b?.winnerSlot ?? null;
      if (sa !== null && sb !== null && sa !== sb) return sa - sb;
      return getWinnerTime(a) - getWinnerTime(b);
    });
  });

  const cards = orderedGroups.slice(0, 5);

  return (
    <motion.div
      className="w-full"
      initial={{ y: 20, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ type: 'spring', stiffness: 300, damping: 24, delay: 0.3 }}
    >
      <div className="w-full flex items-center mb-3 px-2">
        <h2 className="text-[17px] font-medium text-gradient font-['Outfit']">
          Recent Winners
        </h2>
      </div>

      <div
        id="recent-headliners-list"
        className="flex flex-col gap-2"
      >
        {cards.map((group, index) => {
          const isDouble = group.winners.length >= 2;

          if (isDouble) {
            const [winnerA, winnerB] = group.winners;
            return (
              <motion.div
                key={group.key || `group-${index}`}
                data-testid={`winner-card-${index}`}
                data-winners="2"
                className="glass-panel rounded-[24px] px-2 py-2"
                whileHover={{ scale: 1.01 }}
                whileTap={{ scale: 0.99 }}
              >
                <div className="flex items-stretch">
                  <WinnerHalf
                    winner={winnerA}
                    onUserClick={onUserClick}
                    testId={`winner-card-${index}-a`}
                  />
                  <div className="w-px self-stretch bg-black/10 mx-1 flex-shrink-0" />
                  <WinnerHalf
                    winner={winnerB}
                    onUserClick={onUserClick}
                    testId={`winner-card-${index}-b`}
                  />
                </div>
              </motion.div>
            );
          }

          const winner = group.winners[0];
          return (
            <motion.div
              key={group.key || `${winner.username}-${index}`}
              onClick={() => onUserClick(winner)}
              data-testid={`winner-card-${index}`}
              data-winners="1"
              className="glass-panel rounded-full px-3 py-2 cursor-pointer"
              whileHover={{ scale: 1.01 }}
              whileTap={{ scale: 0.99 }}
            >
              <div className="flex items-center gap-3 min-w-0">
                <img
                  src={resolveProfilePic(winner.profilePic)}
                  alt={winner.name}
                  onError={handleProfilePicError}
                  className="w-11 h-11 rounded-full object-cover border-2 border-white flex-shrink-0"
                />
                <div className="flex-1 min-w-0">
                  <h3 className="text-sm font-semibold text-[#000000] font-['Outfit'] truncate">
                    {winner.name}
                  </h3>
                  <p className="text-xs text-[#666666] font-['DM_Sans'] truncate">
                    {winner.username}
                  </p>
                </div>
                <div className="text-right flex-shrink-0 pl-2">
                  <p className="text-base leading-5 font-bold text-gradient font-['Outfit']">
                    {getWinnerDisplayAmount(winner)}
                  </p>
                  <p className="text-[11px] leading-4 text-[#666666] font-['DM_Sans']">
                    {formatWinnerDate(winner)}
                  </p>
                </div>
              </div>
            </motion.div>
          );
        })}
      </div>
    </motion.div>
  );
};

export default PreviousWinners;
